import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Formik, Form } from "formik";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Loader from 'src/utils/Loader';
import { getDefaultTrackedState } from './VariantComponent';
import { Spinner } from "react-bootstrap";

import { useAppDispatch, useAppSelector } from "src/redux/storeHooks";
import {
  getCustomFieldsListAsync,
  getWarehouseListAsync,
} from "src/redux/actions/omsPageActions";
import {
  adjustVariantInventoryAsync,
  createVariantCustomFieldAsync,
  getAllVariantsAsync,
  getCustomEditVariantCustomFieldsAsync,
  getInventoryDetailsAsync,
  getUserGalleryAsync,
  SaveVariantAsync,
} from "src/redux/actions/omsProductActions";
import {
  blockInvalidChar,
  blockInvalidCharWithDecimal,
  buildOptionsFromValue,
  ensureNonNegative,
  buildVariantCustomFieldsPayload,
} from "src/marketConnect/helpers";

import LocationModal from "./LocationsModal";
import SelectProductImageModal from "./SelectedProductImage";
import VariantCustomFields from "./VariantCustomFields";
import InventoryDetailsTable from "./InventoryDetailsTable";
import { usePlatformBackUrl } from "src/hooks/usePlatformBackUrl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faWarehouse } from "@fortawesome/free-solid-svg-icons";
import { DIMENSION_UNIT_OPTIONS, WEIGHT_UNIT_OPTIONS } from "src/utils/constants";
import styles from "./VariantEditorPremium.module.css";
import { Card, CardContent, Tabs, Tab, Box, styled } from "@mui/material";
import InventoryLogs from "../inventory/InventoryLogs";
import PriceLogs from "../price-logs/PriceLogs";
import PremiumBreadcrumb from "src/app/components/navigation/PremiumBreadcrumb";

type InventoryRow = {
  id: number;
  location: string;
  quantity: number | string;
};

type VariantEditorValues = {
  options: Record<string, string>;
  salePrice: number | string;
  costPrice: number | string;
  comparePrice: number | string;
  sku: string;
  barcode: string;
  mpn: string;
  isbn: string;
  ean: string;
  sell_price_id: number | null;
  cost_price_id: number | null;
  compare_price_id: number | null;
  weight_id: number | null;
  weight: number | string;
  weightUnit: string;
  length: number | string;
  width: number | string;
  height: number | string;
  dimensionUnit: string;
  selectedImages: any[];
  trackInventory: boolean;
  inventory: InventoryRow[];
  custom_fields: Record<number, any>;
  customName?: string;
};

type VariantEditorNavState = {
  mode: "add" | "edit";
  productId?: number | string;
  formikData?: any;
  activeVariantIndex?: number;
  variantInternalIndex?: number;
  variantId?: number;
  getEditData?: any;
  rowData?: any;
  platformId?: number | string;
};

type ParentOptionDef = {
  id: number;
  name: string;
  position: number;
  values?: string[];
  value_ids?: Record<string, number>;
};

type Img = {
  fileName: string;
  imageUrl: string;
  url?: string;
  id?: number;
};

const StyledTabs = styled(Tabs)(({ theme }) => ({
  "& .MuiTabs-indicator": {
    backgroundColor: "#007BFF", // bright blue underline
    height: 3,
    borderRadius: 2,
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "15px",
  textTransform: "none",
  color: "#007BFF",
  "&.Mui-selected": {
    color: "#007BFF",
  },
  "&:hover": {
    color: "#0056b3",
  },
  minHeight: 48,
  marginRight: theme.spacing(2),
}));

const normalizeVariants = (res: any): any[] => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.variants)) return res.data.variants;
  return [];
};

const getOrderedOptionDefs = (
  navState: VariantEditorNavState | null,
  variantsFromApi: any[] = []
): ParentOptionDef[] => {
  //console.log("getOrderedOptionDefs called with navState:", navState);

  // 1. First preference: API variant response
  const apiSourceVariant =
    variantsFromApi?.[0] ||
    navState?.getEditData?.variants?.[0] ||
    navState?.rowData;

  if (apiSourceVariant) {
    const apiOptionItems = [
      apiSourceVariant?.platform_product_option_value1,
      apiSourceVariant?.platform_product_option_value2,
      apiSourceVariant?.platform_product_option_value3,
    ].filter(Boolean);

    if (apiOptionItems.length) {
      return apiOptionItems.map((item: any, idx: number) => ({
        id: Number(item?.platform_product_option || idx + 1),
        name: String(item?.option_name || `Option ${idx + 1}`),
        position: idx + 1,
        values: [],
        value_ids: {},
      }));
    }
  }

  // 2. Fallback: parentFormikData/navState formikData options
  const formikOptions = Array.isArray(navState?.formikData?.options)
    ? navState!.formikData.options
    : [];

  if (formikOptions.length) {
    return formikOptions
      .filter((o: any) => o?.id)
      .slice()
      .sort((a: any, b: any) => Number(a.position || 0) - Number(b.position || 0))
      .map((o: any) => ({
        id: Number(o.id),
        name: String(o.name || ""),
        position: Number(o.position || 0),
        values: Array.isArray(o.values) ? o.values : [],
        value_ids: o.value_ids || {},
      }))
      .slice(0, 3);
  }

  // 3. Existing fallback from getEditData.options
  const apiOptionsObj = navState?.getEditData?.options;
  if (apiOptionsObj && typeof apiOptionsObj === "object") {
    return Object.entries(apiOptionsObj)
      .map(([key, value]: [string, any], idx) => ({
        id: Number(value?.id || idx + 1),
        name: key,
        position: idx + 1,
        values: Array.isArray(value?.values)
          ? value.values.map((x: any) => String(x?.value ?? ""))
          : [],
        value_ids: {},
      }))
      .filter((x) => !!x.name)
      .slice(0, 3);
  }

  return [];
};

const getParentVariantRowByIdOrIndex = (
  navState: VariantEditorNavState | null,
  activeVariantId: number | null
) => {
  const rows = Array.isArray(navState?.formikData?.varients_data)
    ? navState!.formikData.varients_data
    : [];

  if (!rows.length) return null;

  if (activeVariantId) {
    const byId = rows.find(
      (row: any) => Number(row?.variant_id) === Number(activeVariantId)
    );
    if (byId) return byId;
  }

  if (navState?.activeVariantIndex != null && rows[navState.activeVariantIndex]) {
    return rows[navState.activeVariantIndex];
  }

  return rows[0] ?? null;
};

const buildOptionsShape = (count: number): Record<string, string> => {
  return {
    part_1: "",
    part_2: "",
    part_3: "",
    ...Object.fromEntries(
      Array.from({ length: count }).map((_, i) => [`part_${i + 1}`, ""])
    ),
  };
};

const splitVariantValueFallback = (raw: string, count: number): string[] => {
  if (!raw) return Array.from({ length: count }, () => "");

  const normalized = raw.replace(/\s*\/\s*/g, "-").replace(/\s*-\s*/g, "-").trim();
  const parts = normalized.split("-").map((x) => x.trim());

  return Array.from({ length: count }, (_, i) => parts[i] ?? "");
};

const hydratePartsFromVariantAndParent = (
  variant: any,
  navState: VariantEditorNavState | null,
  activeVariantId: number | null
): Record<string, string> => {
  const optionDefs = getOrderedOptionDefs(navState, variant ? [variant] : []);
  const out = buildOptionsShape(Math.max(optionDefs.length, 3));

  if (!optionDefs.length) {
    const fallback = buildOptionsFromValue(variant?.name || "");
    return { ...out, ...fallback };
  }

  const platformValues = [
    variant?.platform_product_option_value1,
    variant?.platform_product_option_value2,
    variant?.platform_product_option_value3,
  ].filter(Boolean);

  if (platformValues.length) {
    const optionIdToValue: Record<number, string> = {};
    const optionNameToValue: Record<string, string> = {};

    platformValues.forEach((item: any) => {
      const optionId = Number(item?.platform_product_option);
      const val = String(item?.value || item?.api_id || item?.api_code || "");
      if (optionId) {
        optionIdToValue[optionId] = val;
      }
      if (item?.option_name) {
        optionNameToValue[String(item.option_name).trim().toLowerCase()] = val;
      }
    });

    optionDefs.forEach((opt, idx) => {
      const optNameLower = String(opt.name).trim().toLowerCase();
      out[`part_${idx + 1}`] = optionNameToValue[optNameLower] ?? optionIdToValue[opt.id] ?? "";
    });

    return out;
  }

  const parentRow = getParentVariantRowByIdOrIndex(navState, activeVariantId);
  const fallbackSource = parentRow?.optionValues?.[0] ?? variant?.name ?? "";
  const splitValues = splitVariantValueFallback(fallbackSource, optionDefs.length);

  optionDefs.forEach((_, idx) => {
    out[`part_${idx + 1}`] = splitValues[idx] ?? "";
  });

  return out;
};

const VariantEditorPremium: React.FC = () => {
  const { productId: paramProductId, variantId: paramVariantId } = useParams();
  const location = useLocation();
  const params = useParams();
  //console.log(" params:", params)
  const navState = location.state as VariantEditorNavState | null;

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  //console.log("VariantEditorPremium rendered with params:", navState);

  const effectiveProductId = navState?.productId ?? paramProductId;
  const navMode = navState?.mode;
  const parentFormikData = navState?.formikData;
  const getEditData = navState?.getEditData;

  const isEdit = Boolean(paramVariantId) || navMode === "edit";
  const productIdNum = Number(effectiveProductId || 0);

  const paramPlatformId = params.platformId;
  const effectivePlatformId = navState?.platformId ?? paramPlatformId;
  const backUrl = usePlatformBackUrl(effectivePlatformId);
  const mainProductUrl =
    effectiveProductId && effectivePlatformId
      ? `/update-product/platform/${effectivePlatformId}/productId/${effectiveProductId}`
      : undefined;

  const [inventorySearch, setInventorySearch] = useState("");
  const [isPhysicalProduct, setIsPhysicalProduct] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showInventoryPopup, setShowInventoryPopup] = useState(false);
  const [isSavingVariant, setIsSavingVariant] = useState(false);
  const isSavingVariantRef = useRef(false);
  const [variantsLoading, setVariantsLoading] = useState(false);
  const [, setVariantsError] = useState<string>("");
  const [variants, setVariants] = useState<any[]>([]);
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageLibrary, setImageLibrary] = useState<any[]>([]);
  const [allCustomFields, setAllCustomFields] = useState<any[]>([]);
  const [editCustomFields, setEditCustomFields] = useState<any[]>([]);

  const [activeVariantId, setActiveVariantId] = useState<number | null>(() => {
    if (paramVariantId) return Number(paramVariantId);
    if (navState?.variantId) return Number(navState.variantId);
    return getEditData?.variants?.[0]?.id ?? null;
  });

  const [activeTab, setActiveTab] = useState(0);
  const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const [initialValues, setInitialValues] = useState<VariantEditorValues>({
    options: { part_1: "", part_2: "", part_3: "" },
    salePrice: "",
    costPrice: "",
    comparePrice: "",
    sku: "",
    barcode: "",
    mpn: "",
    isbn: "",
    ean: "",
    sell_price_id: null,
    cost_price_id: null,
    compare_price_id: null,
    weight_id: null,
    selectedImages: [],
    trackInventory: true,
    inventory: [],
    custom_fields: {},
    weight: "",
    weightUnit: "KGS",
    length: "",
    width: "",
    height: "",
    dimensionUnit: "CM",
    customName: "",
  });

  const uniqBy = <T,>(arr: T[], keyFn: (x: T) => string) => {
    const seen = new Set<string>();
    const out: T[] = [];

    for (const x of arr) {
      const key = keyFn(x);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(x);
    }

    return out;
  };

  const optionDefs = useMemo(
    () => getOrderedOptionDefs(navState, variants),
    [navState, variants]
  );

  const LIMIT = 20;

  const mapRowsToImgs = (rows: any[] = []): Img[] => {
    return rows
      .filter((row: any) => {
        const mime = String(row?.mime_type || "").toLowerCase();
        return mime.startsWith("image/") && row?.file_path;
      }).map((row: any) => ({
        id: row?.id,
        fileName: row?.file_description || `image-${row?.id ?? ""}`,
        imageUrl: row?.file_path,
        url: row?.file_path,
      }));
  };

  const fetchGalleryImages = useCallback(
    async (page: number, search: string) => {
      const res = await dispatch(
        getUserGalleryAsync({
          page,
          limit: LIMIT,
          search: (search || "").trim(),
        })
      ).unwrap();

      const rows = res?.results?.data || [];

      return {
        list: mapRowsToImgs(rows),
        hasMore: Boolean(res?.next),
      };
    },
    [dispatch]
  );

  const mapVariantToFormikBase = useCallback(
    (variant: any, currentVariantId: number | null): VariantEditorValues => {
      const matchedParentRow = getParentVariantRowByIdOrIndex(navState, currentVariantId);
      const dynamicOptions = hydratePartsFromVariantAndParent(variant, navState, currentVariantId);

      const sellObj = variant?.sell_price ?? variant?.price ?? null;
      const costObj = variant?.cost_price ?? null;
      const compareObj = variant?.compare_price ?? variant?.compare_price ?? null;

      const parentImage = matchedParentRow?.variant_image;

      return {
        options: dynamicOptions,
        salePrice: ensureNonNegative(sellObj?.value ?? matchedParentRow?.price ?? ""),
        costPrice: ensureNonNegative(costObj?.value ?? ""),
        comparePrice: ensureNonNegative(compareObj?.value ?? ""),
        sku: variant?.sku || matchedParentRow?.sku || "",
        barcode: variant?.barcode || matchedParentRow?.barcode || "",
        mpn: variant?.mpn || matchedParentRow?.mpn || "",
        isbn: variant?.isbn || matchedParentRow?.isbn || "",
        ean: variant?.ean || matchedParentRow?.ean || "",
        sell_price_id: sellObj?.id ?? null,
        cost_price_id: costObj?.id ?? null,
        compare_price_id: compareObj?.id ?? null,
        weight: ensureNonNegative(variant?.weight?.value ?? ""),
        weight_id: variant?.weight?.id ?? null,
        weightUnit: variant?.weight?.unit ?? "KGS",

        length: ensureNonNegative(variant?.length?.api_id ?? ""),
        width: ensureNonNegative(variant?.width?.api_id ?? ""),
        height: ensureNonNegative(variant?.height?.api_id ?? ""),
        dimensionUnit: variant?.dimention_unit?.api_id ?? "CM",

        selectedImages: variant?.image?.image_url
          ? [
            {
              fileName: `server-variant-${variant?.image?.id ?? variant?.id ?? ""}`,
              imageUrl: variant.image.image_url,
              url: variant.image.image_url,
              id: variant.image.id,
              type: "image/*",
              size: 0,
              isExistingVariantImage: true, // important
            },
          ]
          : parentImage?.imageUrl
            ? [
              {
                fileName: parentImage.fileName || `server-variant-${variant?.id ?? ""}`,
                imageUrl: parentImage.imageUrl,
                url: parentImage.url || parentImage.imageUrl,
                id: parentImage.id,
                type: "image/*",
                size: 0,
                isExistingVariantImage: true, // important
              },
            ]
            : [],
        trackInventory: variant?.isTracked !== undefined
          ? Boolean(variant.isTracked)
          : getDefaultTrackedState(variants, getEditData?.isTracked ?? true),
        inventory: [],
        custom_fields: {},
        customName: variant?.name ?? "",
      };
    },
    [navState]
  );

  const buildImageLibrary = useCallback((parentData: any, editData: any): Img[] => {
    const fromParent: Img[] = (parentData?.selectedImages || []).map((img: any) => ({
      id: img.id,
      fileName: img.fileName || `server-${img.id ?? ""}`,
      imageUrl: img.imageUrl || img.url,
      url: img.url || img.imageUrl,
    }));

    const fromEditData: Img[] = (editData?.images || []).map((img: any) => ({
      id: img.id,
      fileName: `server-${img.id}`,
      imageUrl: img.image_url,
      url: img.image_url,
    }));

    const fromVariants: Img[] = (editData?.variants || [])
      .map((v: any) => v?.image?.image_url)
      .filter(Boolean)
      .map((url: string, idx: number) => ({
        fileName: `variant-${idx + 1}`,
        imageUrl: url,
        url,
      }));

    return uniqBy([...fromParent, ...fromEditData, ...fromVariants], (x) => x.url || x.imageUrl);
  }, []);

  const fetchAllWarehouses = useCallback(
    async (search = "") => {
      let page = 1;
      let hasNext = true;
      const all: any[] = [];

      while (hasNext) {
        const res: any = await dispatch(
          getWarehouseListAsync({
            paramData: {
              page,
              limit: 50,
              search,
              sort_by: "id",
              sort_order: "asc",
              warehouse_id: null,
              isActive: true,
            },
          })
        );

        const payload = res?.payload;
        const rows =
          payload?.results?.data ||
          payload?.data?.results?.data ||
          payload?.data ||
          payload?.results ||
          [];

        all.push(...rows);

        hasNext = payload?.next !== null && payload?.next !== undefined;
        page += 1;

        if (!rows.length) break;
      }

      return all;
    },
    [dispatch]
  );

  const mapAllWarehousesToRows = useCallback((warehouses: any[] = []): InventoryRow[] => {
    return warehouses.map((w: any) => ({
      id: Number(w.id),
      location: String(w.api_name ?? w.location_name ?? "Warehouse"),
      quantity: 0,
    }));
  }, []);

  const mergeWarehousesWithInventory = useCallback(
    (
      warehouses: any[] = [],
      invRes: any,
      currentVariantId: number | null
    ): InventoryRow[] => {
      const allWarehouseRows = mapAllWarehousesToRows(warehouses);

      const variantRows = Array.isArray(invRes?.results?.data)
        ? invRes.results.data
        : [];
      const matchedVariant =
        variantRows.find((v: any) => Number(v?.id) === Number(currentVariantId)) ||
        variantRows[0];

      const locations = Array.isArray(matchedVariant?.locations)
        ? matchedVariant.locations
        : [];

      if (!locations.length) return allWarehouseRows;

      const locMap = new Map<number, any>();
      locations.forEach((loc: any) => {
        locMap.set(Number(loc.location_id), loc);
      });

      return allWarehouseRows.map((row) => {
        const loc = locMap.get(Number(row.id));
        return {
          ...row,
          quantity: loc ? Number(loc.available_quantity ?? 0) : 0,
          reserve_quantity: loc ? Number(loc.reserve_quantity ?? 0) : 0,
          on_hand: loc ? Number(loc.on_hand ?? 0) : 0,
        };
      });
    },
    [mapAllWarehousesToRows]
  );

  const buildAdjustInventoryPayloadForVariant = (
    values: VariantEditorValues,
    productId: number,
    variantId: number
  ) => {
    const sku = values?.sku || "";

    return {
      convert: false,
      items: (values.inventory || [])
        .filter((row: any) => Boolean(row?.id))
        .map((row: any) => ({
          variant_id: Number(variantId),
          product_id: Number(productId),
          sku,
          quantity: Number(row?.quantity) || 0,
          warehouse_id: Number(row?.id),
        })),
    };
  };

  const getSavedVariantIdFromResponse = (
    res: any,
    fallbackVariantId?: number | null
  ) => {
    return Number(
      res?.data?.[0]?.id ??
      res?.data?.[0] ??
      res?.data?.id ??
      res?.results?.data?.[0]?.id ??
      res?.results?.data?.[0] ??
      res?.results?.data?.id ??
      res?.id ??
      fallbackVariantId ??
      0
    );
  };

  useEffect(() => {
    const shouldFetchVariantsForOptionDefs =
      isEdit ||
      (!navState?.getEditData?.variants?.length &&
        !navState?.rowData &&
        Number(effectiveProductId));

    if (!shouldFetchVariantsForOptionDefs) return;

    (async () => {
      try {
        setVariantsLoading(true);
        setVariantsError("");

        const res = await dispatch(
          getAllVariantsAsync({ id: Number(effectiveProductId) })
        ).unwrap();

        setVariants(normalizeVariants(res));
      } catch (e: any) {
        setVariantsError(e?.message || "Failed to fetch variants");
        setVariants([]);
      } finally {
        setVariantsLoading(false);
      }
    })();
  }, [
    dispatch,
    effectiveProductId,
    isEdit,
    navState?.getEditData?.variants?.length,
    navState?.rowData,
  ]);

  useEffect(() => {
    setImageLibrary(buildImageLibrary(parentFormikData, getEditData));
  }, [parentFormikData, getEditData, buildImageLibrary]);

  useEffect(() => {
    setIsPhysicalProduct(Boolean(initialValues.trackInventory));
  }, [initialValues.trackInventory]);

  useEffect(() => {
    if (isEdit) return;

    (async () => {
      try {
        const warehouses = await fetchAllWarehouses("");
        const rows = mapAllWarehousesToRows(warehouses);

        setInitialValues((prev) => ({
          ...prev,
          inventory: rows,
          trackInventory: true,
        }));
      } catch {
        // ignore
      }
    })();
  }, [isEdit, fetchAllWarehouses, mapAllWarehousesToRows]);

  useEffect(() => {
    if (isEdit) return;

    setInitialValues((prev) => ({
      options: buildOptionsShape(Math.max(optionDefs.length, 3)),
      salePrice: "",
      costPrice: "",
      comparePrice: "",
      sku: "",
      barcode: "",
      mpn: "",
      isbn: "",
      ean: "",
      sell_price_id: null,
      cost_price_id: null,
      compare_price_id: null,
      weight_id: null,
      selectedImages: [],
      trackInventory: true,
      inventory: prev.inventory,
      custom_fields: {},
      weight: "",
      weightUnit: "KGS",
      length: "",
      width: "",
      height: "",
      dimensionUnit: "CM",
    }));
  }, [isEdit, optionDefs.length]);

  useEffect(() => {
    if (!isEdit) return;

    if (paramVariantId) {
      setActiveVariantId(Number(paramVariantId));
      return;
    }

    if (navState?.variantId) {
      setActiveVariantId(Number(navState.variantId));
      return;
    }

    if (activeVariantId == null && getEditData?.variants?.length) {
      setActiveVariantId(Number(getEditData.variants[0].id));
    }
  }, [isEdit, paramVariantId, navState?.variantId, getEditData, activeVariantId]);

  useEffect(() => {
    if (!isEdit || !activeVariantId || !productIdNum) return;

    (async () => {
      try {
        setLoading(true);

        const variant =
          variants.find((x: any) => Number(x.id) === Number(activeVariantId)) ||
          getEditData?.variants?.find(
            (x: any) => Number(x.id) === Number(activeVariantId)
          );

        if (!variant) return;

        const base = mapVariantToFormikBase(variant, Number(activeVariantId));
        setIsPhysicalProduct(Boolean(base.trackInventory));

        const [allWarehouses, invRes] = await Promise.all([
          fetchAllWarehouses(""),
          dispatch(
            getInventoryDetailsAsync({
              page: 1,
              limit: 50,
              sort_by: "desc",
              order_by: "id",
              variant_ids: String(activeVariantId),
              search: "",
            })
          ).unwrap(),
        ]);

        const inventoryRows = mergeWarehousesWithInventory(
          allWarehouses,
          invRes,
          Number(activeVariantId)
        );

        setInitialValues({
          ...base,
          trackInventory: Boolean(base.trackInventory),
          inventory: inventoryRows.length ? inventoryRows : base.inventory,
        });
      } catch (e: any) {
        toast.error(e?.message || "Failed to load variant details");
      } finally {
        setLoading(false);
      }
    })();
  }, [
    isEdit,
    activeVariantId,
    productIdNum,
    dispatch,
    getEditData,
    variants,
    mapVariantToFormikBase,
    fetchAllWarehouses,
    mergeWarehousesWithInventory,
  ]);

  useEffect(() => {
    (async () => {
      try {
        const res = await dispatch(
          getCustomFieldsListAsync({
            paramData: {
              page: 1,
              limit: 100,
              search: "",
              sort_order: "",
              sort_by: "",
              isActive: true,
              owner: "PRODUCTVARIANT",
            },
          })
        ).unwrap();

        const list = res?.data?.results?.data || res?.results?.data || res?.data?.data || res?.data || res?.results || res || [];

        setAllCustomFields(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error("Failed to load all custom fields", err);
        setAllCustomFields([]);
      }
    })();
  }, [dispatch]);

  useEffect(() => {
    if (!isEdit || !activeVariantId) {
      setEditCustomFields([]);
      return;
    }

    (async () => {
      try {
        const res = await dispatch(
          getCustomEditVariantCustomFieldsAsync({
            productId: Number(productIdNum),
            variantId: Number(activeVariantId),
          })
        ).unwrap();

        const list = res?.data?.results?.data || res?.results?.data || res?.data?.data || res?.data ||
          res?.results || res || [];

        setEditCustomFields(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error("Failed to load edit custom fields", err);
        setEditCustomFields([]);
      }
    })();
  }, [dispatch, isEdit, productIdNum, activeVariantId]);

  const validateBeforeSave = (values: VariantEditorValues) => {
    const dynamicOptionDefs = optionDefs.length
      ? optionDefs
      : Object.keys(values.options || {})
        .sort((a, b) => Number(a.split("_")[1]) - Number(b.split("_")[1]))
        .filter((key) => values.options[key] !== undefined)
        .slice(0, 3)
        .map((key, index) => ({
          id: index + 1,
          name: `Option ${index + 1}`,
          position: index + 1,
          values: [],
          value_ids: {},
        }));

    for (let idx = 0; idx < dynamicOptionDefs.length; idx++) {
      const opt = dynamicOptionDefs[idx];
      const partKey = `part_${idx + 1}`;
      const labelBase = opt?.name?.trim()
        ? `${opt.name.charAt(0).toUpperCase()}${opt.name.slice(1)}`
        : `Option ${idx + 1}`;

      if (!String(values.options?.[partKey] ?? "").trim()) {
        return `${labelBase} is required`;
      }
    }

    if (!String(values.sku ?? "").trim()) {
      return "SKU Code is required";
    }

    if (
      values.salePrice === "" ||
      values.salePrice === null ||
      values.salePrice === undefined
    ) {
      return "Sale Price is required";
    }

    // if (
    //   values.costPrice === "" ||
    //   values.costPrice === null ||
    //   values.costPrice === undefined
    // ) {
    //   return "Cost Price is required";
    // }

    // if (
    //   values.comparePrice === "" ||
    //   values.comparePrice === null ||
    //   values.comparePrice === undefined
    // ) {
    //   return "Compare Price is required";
    // }

    const requiredCustomFields = (allCustomFields || []).filter(
      (field: any) =>
        field?.isActive &&
        field?.owner === "PRODUCTVARIANT" &&
        field?.isRequired
    );

    for (const field of requiredCustomFields) {
      const fieldValue = values?.custom_fields?.[field.id];
      const label = String(field?.display_name || "Custom field").trim();

      if (field?.input_type === "CHECKBOX") {
        if (fieldValue !== true) {
          return `${label} is required`;
        }
        continue;
      }

      if (field?.input_type === "MULTISELECT") {
        if (!Array.isArray(fieldValue) || fieldValue.length === 0) {
          return `${label} is required`;
        }
        continue;
      }

      if (field?.input_type === "SELECT") {
        if (
          fieldValue === undefined ||
          fieldValue === null ||
          String(fieldValue).trim() === ""
        ) {
          return `${label} is required`;
        }
        continue;
      }

      if (field?.input_type === "NUMBER") {
        if (
          fieldValue === undefined ||
          fieldValue === null ||
          String(fieldValue).trim() === ""
        ) {
          return `${label} is required`;
        }
        continue;
      }

      if (
        fieldValue === undefined ||
        fieldValue === null ||
        String(fieldValue).trim() === ""
      ) {
        return `${label} is required`;
      }
    }

    return null;
  };

  const buildOptionValuePayload = (
    vals: VariantEditorValues,
    optionDefs: ParentOptionDef[]
  ): Record<string, string> => {
    return optionDefs.reduce((acc, opt, index) => {
      const partKey = `part_${index + 1}`;
      const optionName = String(opt?.name || "").trim();
      const optionVal = String(vals.options?.[partKey] ?? "").trim();

      if (optionName && optionVal) {
        acc[optionName] = optionVal;
      }

      return acc;
    }, {} as Record<string, string>);
  };

  const buildSaveVariantPayload = (
    vals: VariantEditorValues,
    isEditMode: boolean,
    currentActiveVariantId: number | null,
    optionDefs: ParentOptionDef[]
  ) => {
    const autoGeneratedName = Object.keys(vals.options)
      .sort((a, b) => Number(a.split("_")[1]) - Number(b.split("_")[1]))
      .map((k, index) => {
        const val = vals.options[k];
        if (!val) return null;
        const optName = optionDefs[index]?.name || `Option ${index + 1}`;
        return `${optName}: ${val}`;
      })
      .filter(Boolean)
      .join(" / ");

    const variantName = vals.customName || autoGeneratedName;

    const option_value = buildOptionValuePayload(vals, optionDefs);

    const currentVariant =
      getEditData?.variants?.find(
        (v: any) => Number(v?.id) === Number(currentActiveVariantId)
      ) || variants.find((v: any) => Number(v?.id) === Number(currentActiveVariantId));

    const originalVariantImageId = currentVariant?.image?.id ?? null;

    const variant_images = (vals.selectedImages || [])
      .filter((img: any) => img?.imageUrl || img?.url)
      .map((img: any) => {
        const image_url = img?.imageUrl || img?.url || "";

        if (isEditMode) {
          return {
            image_position: null,
            image_url,
            id: originalVariantImageId,
          };
        }

        return {
          image_position: null,
          image_url,
        };
      });

    console.log("work", {
      vals,
      isEdit: isEditMode,
      currentActiveVariantId,
      originalVariantImageId,
      variant_images,
    });

    return [
      {
        ...(isEditMode ? { id: Number(currentActiveVariantId) } : {}),
        name: variantName,
        option_value,
        barcode: vals.barcode,
        mpn: vals.mpn,
        isbn: vals.isbn,
        ean: vals.ean,
        sku: vals.sku,
        isActive: true,
        isTracked: vals.trackInventory,
        position: 1,
        variant_images,

        gtin_number: null,

        weight: {
          ...(isEditMode && vals.weight_id != null ? { id: Number(vals.weight_id) } : {}),
          value: Number(vals.weight || 0),
        },
        weight_unit: vals.weightUnit || "KGS",

        dimensions: [
          { length: String(vals.length ?? "") },
          { width: String(vals.width ?? "") },
          { height: String(vals.height ?? "") },
        ],

        dimensions_unit: vals.dimensionUnit || "CM",

        sell_price: {
          ...(isEditMode && vals.sell_price_id != null ? { id: Number(vals.sell_price_id) } : {}),
          value: Number(vals.salePrice || 0),
        },
        cost_price: {
          ...(isEditMode && vals.cost_price_id != null ? { id: Number(vals.cost_price_id) } : {}),
          value: Number(vals.costPrice || 0),
        },
        compare_price: {
          ...(isEditMode && vals.compare_price_id != null ? { id: Number(vals.compare_price_id) } : {}),
          value: Number(vals.comparePrice || 0),
        },
      },
    ];
  };

  const saveHandler = async (values: VariantEditorValues, helpers: any) => {
    if (isSavingVariantRef.current) return;
    isSavingVariantRef.current = true;
    setIsSavingVariant(true);
    helpers.setSubmitting(true);

    let cooldown = 0;

    try {
      const validationMessage = validateBeforeSave(values);
      if (validationMessage) {
        toast.error(validationMessage);
        cooldown = 4000;
        return;
      }

      const variantsList = buildSaveVariantPayload(values, isEdit, activeVariantId, optionDefs);

      const saveRes: any = await dispatch(
        SaveVariantAsync({
          product_id: Number(effectiveProductId), payload: variantsList,
        })).unwrap();

      const status = Number(saveRes?.status);
      const apiMessage =
        saveRes?.message || saveRes?.data?.message || "Save failed";

      if (status !== 200 && status !== 201) {
        toast.error(apiMessage);
        return;
      }

      const savedVariantId = getSavedVariantIdFromResponse(
        saveRes,
        isEdit ? activeVariantId : null
      );

      if (!savedVariantId || !productIdNum) {
        throw new Error("Missing variantId or productId");
      }

      const adjustPayload = buildAdjustInventoryPayloadForVariant(
        values, productIdNum, savedVariantId);

      if (values.trackInventory && adjustPayload.items.length > 0) {
        await dispatch(adjustVariantInventoryAsync({ data: adjustPayload })).unwrap();
      }

      const customFieldsPayload = buildVariantCustomFieldsPayload({
        mode: isEdit ? "edit" : "create",
        customFieldsObj: values.custom_fields || {},
        customFieldsList: allCustomFields || [],
        existingCustomFields: editCustomFields || [],
      });
      console.log("Custom fields payload:", customFieldsPayload);

      if (customFieldsPayload.length > 0) {
        await dispatch(
          createVariantCustomFieldAsync({
            variantId: savedVariantId,
            data: customFieldsPayload,
          })
        ).unwrap();
      }

      toast.success(apiMessage);
      if (!isEdit) {
        navigate(`/update-variant/platform/${effectivePlatformId}/productId/${effectiveProductId}/variantId/${savedVariantId}`);
      } else {
        setInitialValues(values);
      }
    } catch (e: any) {
      toast.error(e?.data?.message || e?.response?.data?.message || e?.message || "Save failed");
    } finally {
      if (cooldown > 0) {
        setTimeout(() => {
          isSavingVariantRef.current = false;
          setIsSavingVariant(false);
          helpers.setSubmitting(false);
        }, cooldown);
      } else {
        isSavingVariantRef.current = false;
        setIsSavingVariant(false);
        helpers.setSubmitting(false);
      }
    }
  };

  if (loading || variantsLoading) {
    return (
      <div className="variantScope">
        <div className="variantScope__loader">
          <Loader variant="section" />
        </div>
      </div>
    );
  }

  console.log("Rendering with initialValues:", initialValues);

  return (
    <div className="variantScope page page--create-product d-flex flex-column flex-column-fluid">
      <div className="variantScope__scroll">
        <div className="px-4 pb-6">
          <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={(vals, helpers) => saveHandler(vals, helpers)}
          >
            {({ values, handleChange, setFieldValue, setFormikState, isSubmitting, }) => {
              const dynamicOptionDefs = optionDefs.length
                ? optionDefs
                : Object.keys(values.options || {}).sort(
                  (a, b) =>
                    Number(a.split("_")[1]) - Number(b.split("_")[1])
                ).filter((key) => values.options[key] !== undefined)
                  .slice(0, 3).map((key, index) => ({
                    id: index + 1,
                    name: `Option ${index + 1}`,
                    position: index + 1,
                    values: [],
                    value_ids: {},
                  }));

              const autoGeneratedNameUI = Object.keys(values.options || {})
                .sort((a, b) => Number(a.split("_")[1]) - Number(b.split("_")[1]))
                .map((k, index) => {
                  const val = values.options[k];
                  if (!val) return null;
                  const optName = dynamicOptionDefs[index]?.name || `Option ${index + 1}`;
                  return `${optName}: ${val}`;
                })
                .filter(Boolean)
                .join(" / ");

              const visibleOptionDefs = dynamicOptionDefs.slice(
                0,
                optionDefs.length ? optionDefs.length : dynamicOptionDefs.length
              );

              const handleQuantityChange = (
                id: number,
                value: number | string
              ) => {
                const updatedInventory = (values.inventory || []).map((row) =>
                  row.id === id ? { ...row, quantity: value } : row
                );
                setFieldValue("inventory", updatedInventory);
              };

              const handleSaveInventoryLocations = (
                selectedLocations: { id: number; api_name: string }[]
              ) => {
                const rows = selectedLocations.map((loc) => ({
                  id: loc.id, location: loc.api_name, quantity: 0,
                }));

                setFieldValue("inventory", rows);
                setShowInventoryPopup(false);
              };

              const filteredInventoryRows = (values.inventory || []).filter((row) =>
                String(row.location || "").toLowerCase().includes(inventorySearch.toLowerCase())
              );

              const onInventoryToggleChange = () => {
                const next = !isPhysicalProduct;
                setIsPhysicalProduct(next);
                setFieldValue("trackInventory", next);
              };

              return (
                <Form id="kt_ecommerce_add_product_form">
                  <div className="variantScope__header">
                    <div className="d-flex align-items-center w-100 px-4">
                      <div className="d-flex flex-column">
                        {mainProductUrl && (
                          <PremiumBreadcrumb
                            items={[
                              { label: "Main Product", to: mainProductUrl },
                              {
                                label: isEdit
                                  ? `Variant #${activeVariantId || paramVariantId}`
                                  : "Create Variant",
                              },
                            ]}
                          />
                        )}
                        <span className="fw-bold" style={{ fontSize: 16 }}>
                          {isEdit ? "Update Variant" : "Create Variant"}
                        </span>
                      </div>

                      <div className="ms-auto d-flex align-items-center gap-3">
                        <button
                          type="button"
                          className="btn btn-sm btn-light fw-bold"
                          onClick={() =>
                            navigate(backUrl)
                          }
                          disabled={isSavingVariant}
                        >Cancel
                        </button>

                        <button type="submit" form="kt_ecommerce_add_product_form"
                          className="btn btn-sm fw-bold btn-primary" disabled={isSubmitting || isSavingVariant}
                        >{(isSubmitting || isSavingVariant) ? "Saving..." : isEdit ? "Update Variant" : "Create Variant"}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className={styles.wrap}>
                    <div className={styles.panel}>
                      <div className={styles.header}>
                        <h2 className={styles.headerTitle}>Variant Identity</h2>
                        <div className="d-flex align-items-center gap-3 ms-auto" style={{ width: '350px' }}>
                          <span className="text-gray-700 fs-6 fw-bold text-nowrap">Variant Name</span>
                          <input
                            type="text"
                            name="customName"
                            className="form-control form-control-solid form-control-sm"
                            placeholder="Variant Name"
                            value={values.customName || autoGeneratedNameUI}
                            onChange={(e) => {
                              setFieldValue("customName", e.target.value);
                            }}
                          />
                        </div>
                      </div>

                      <div className={styles.panelBody}>
                        <div className={styles.variantIdentityGrid}>
                          <div className={styles.variantImageCol}>
                            <div
                              className={styles.variantImageBox}
                              role="button"
                              tabIndex={0}
                              onClick={() => {
                                setShowImageModal(true);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  setShowImageModal(true);
                                }
                              }}
                            >
                              {values.selectedImages?.[0]?.url ||
                                values.selectedImages?.[0]?.imageUrl ? (
                                <img
                                  src={values.selectedImages[0].url || values.selectedImages[0].imageUrl
                                  }
                                  alt=""
                                />
                              ) : (
                                <div className={styles.variantImagePlaceholder}>
                                  <div className={styles.cameraIcon}>📷</div>
                                  <div className={styles.uploadText}>
                                    Upload Image
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className={styles.variantFieldsCol}>
                            <div className={styles.twoColRow}>
                              {visibleOptionDefs.map((opt, idx) => {
                                const partKey = `part_${idx + 1}`;
                                const labelBase = opt?.name?.trim()
                                  ? `${opt.name.charAt(0).toUpperCase()}${opt.name.slice(
                                    1)}` : `Option ${idx + 1}`;

                                return (
                                  <div key={`${opt.id}-${partKey}`}>
                                    <label className={`${styles.label} required`}>
                                      {labelBase}
                                    </label>
                                    <input
                                      className={styles.input}
                                      name={`options.${partKey}`}
                                      value={values.options?.[partKey] ?? ""}
                                      onChange={handleChange}
                                      placeholder={
                                        Array.isArray(opt.values) && opt.values.length
                                          ? opt.values[0] : `Enter ${labelBase.toLowerCase()}`
                                      }
                                      list={opt.values?.length
                                        ? `variant-option-list-${opt.id}` : undefined
                                      }
                                    />

                                    {Array.isArray(opt.values) &&
                                      opt.values.length > 0 && (
                                        <datalist id={`variant-option-list-${opt.id}`}>
                                          {opt.values.map((value) => (
                                            <option key={value} value={value} />
                                          ))}
                                        </datalist>
                                      )}
                                  </div>
                                );
                              })}
                            </div>

                            <div className={styles.twoColRow}>
                              <div>
                                <label className={`${styles.label} required`}> SKU Code </label>
                                <input
                                  className={styles.input}
                                  name="sku"
                                  value={values.sku ?? ""}
                                  onChange={handleChange}
                                  placeholder="TSH-001-S-BLU"
                                />
                              </div>

                              <div>
                                <label className={styles.label}>Barcode</label>
                                <input
                                  className={styles.input}
                                  name="barcode"
                                  value={values.barcode ?? ""}
                                  onChange={handleChange}
                                  placeholder="Barcode"
                                />
                              </div>
                            </div>

                            <div className={styles.twoColRow}>
                              <div>
                                <label className={styles.label}>EAN</label>
                                <input
                                  className={styles.input}
                                  name="ean"
                                  value={values.ean ?? ""}
                                  onChange={handleChange}
                                  placeholder="European Article Number"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className={styles.topTwoColRow}>
                      {/* Pricing */}
                      <div className={styles.topTwoColItem}>
                        <div className={styles.panel}>
                          <div className={styles.header}>
                            <h2 className={styles.headerTitle}>Pricing</h2>
                          </div>

                          <div className={styles.panelBody}>
                            <div className={styles.twoColRow}>
                              <div>
                                <label className={`${styles.label} required`}>Sale Price</label>
                                <input
                                  type="number"
                                  min={0}
                                  step="0.01"
                                  onKeyDown={blockInvalidCharWithDecimal}
                                  name="salePrice"
                                  className={styles.input}
                                  placeholder="0"
                                  value={values.salePrice}
                                  onChange={handleChange}
                                />
                              </div>

                              <div>
                                <label className={styles.label}>Cost Price</label>
                                <input
                                  type="number"
                                  min={0}
                                  step="0.01"
                                  onKeyDown={blockInvalidCharWithDecimal}
                                  name="costPrice"
                                  className={styles.input}
                                  placeholder="0"
                                  value={values.costPrice}
                                  onChange={handleChange}
                                  inputMode="decimal"
                                />
                              </div>
                            </div>

                            <div className="mt-4">
                              <div className={styles.twoColRow}>
                                <div>
                                  <label className={styles.label}>Compare Price</label>
                                  <input
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    onKeyDown={blockInvalidCharWithDecimal}
                                    name="comparePrice"
                                    className={styles.input}
                                    placeholder="0"
                                    value={values.comparePrice}
                                    onChange={handleChange}
                                    inputMode="decimal"
                                  />
                                </div>
                                <div />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Shipping & Dimensions */}
                      <div className={styles.topTwoColItem}>
                        <div className={styles.panel}>
                          <div className={styles.header}>
                            <h2 className={styles.headerTitle}>Shipping & Dimensions</h2>
                          </div>

                          <div className={styles.panelBody}>
                            <div>
                              <label className={styles.label}>Product weight</label>

                              <div className={styles.weightRow}>
                                <input
                                  type="number"
                                  name="weight"
                                  className={styles.input}
                                  placeholder="0.0"
                                  value={values.weight}
                                  onChange={handleChange}
                                  onKeyDown={blockInvalidCharWithDecimal}
                                  inputMode="decimal"
                                  min="0"
                                  step="0.01"
                                />

                                <div className={styles.unitSelectWrap}>
                                  <select
                                    name="weightUnit"
                                    className={styles.unitSelect}
                                    value={values.weightUnit}
                                    onChange={handleChange}
                                  >
                                    {WEIGHT_UNIT_OPTIONS.map((unit) => (
                                      <option key={unit.value} value={unit.value}>
                                        {unit.label}
                                      </option>
                                    ))}
                                  </select>

                                  <span className={styles.unitSelectIcon}>
                                    <FontAwesomeIcon icon={faChevronDown} />
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="mt-4">
                              <div className={styles.fourColRow}>
                                <div>
                                  <label className={styles.label}>Length</label>
                                  <input
                                    type="number"
                                    name="length"
                                    className={styles.input}
                                    placeholder="0.0"
                                    value={values.length ?? ""}
                                    onChange={handleChange}
                                    onKeyDown={blockInvalidCharWithDecimal}
                                    inputMode="decimal"
                                    min="0"
                                    step="0.01"
                                  />
                                </div>

                                <div>
                                  <label className={styles.label}>Width</label>
                                  <input
                                    type="number"
                                    name="width"
                                    className={styles.input}
                                    placeholder="0.0"
                                    value={values.width ?? ""}
                                    onChange={handleChange}
                                    onKeyDown={blockInvalidCharWithDecimal}
                                    inputMode="decimal"
                                    min="0"
                                    step="0.01"
                                  />
                                </div>

                                <div>
                                  <label className={styles.label}>Height</label>
                                  <input
                                    type="number"
                                    name="height"
                                    className={styles.input}
                                    placeholder="0.0"
                                    value={values.height ?? ""}
                                    onChange={handleChange}
                                    onKeyDown={blockInvalidCharWithDecimal}
                                    inputMode="decimal"
                                    min="0"
                                    step="0.01"
                                  />
                                </div>

                                <div>
                                  <label className={styles.label}>Unit</label>
                                  <div className={styles.unitSelectWrap}>
                                    <select
                                      name="dimensionUnit"
                                      className={styles.unitSelect}
                                      value={values.dimensionUnit ?? "CM"}
                                      onChange={handleChange}
                                    >
                                      {DIMENSION_UNIT_OPTIONS.map((unit) => (
                                        <option key={unit.value} value={unit.value}>
                                          {unit.label}
                                        </option>
                                      ))}
                                    </select>

                                    <span className={styles.unitSelectIcon}>
                                      <FontAwesomeIcon icon={faChevronDown} />
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className={styles.panel}>
                      <div className={styles.header}>
                        <div className="d-flex justify-content-between align-items-center w-100">
                          <h2
                            className={styles.headerTitle}
                            style={{ marginBottom: 0 }}
                          >Inventory Details
                          </h2>

                          {values.trackInventory && (
                            <div className="mx-3 flex-grow-1" style={{ maxWidth: '400px' }}>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Search inventory location..."
                                value={inventorySearch}
                                onChange={(e) => setInventorySearch(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                  }
                                }}
                              />
                            </div>
                          )}

                          <div className="d-flex align-items-center gap-2">
                            <label
                              className="form-check-label text-muted mb-0"
                              htmlFor="inventoryToggle"
                              style={{ fontSize: "13px", fontWeight: 500 }}
                            >Track Inventory
                            </label>

                            <div className="form-check form-switch m-0">
                                <input
                                  className="form-check-input h-20px w-40px"
                                  type="checkbox"
                                  id="inventoryToggle"
                                  checked={values.trackInventory}
                                  onChange={onInventoryToggleChange}
                                  style={{ cursor: "pointer" }}
                                />
                            </div>
                          </div>
                        </div>
                      </div>
                      {values.trackInventory ? (
                        <div className={styles.panelBody}>
                          <div
                            style={{
                              border: "1px solid #E9EDF5",
                              borderRadius: "14px",
                              padding: "12px",
                              background: "#fff",
                            }}
                          >

                            <div
                              style={{
                                border: "1px solid #E9EDF5",
                                borderRadius: "12px",
                                overflow: "hidden",
                                background: "#FCFDFE",
                              }}
                            >
                              <InventoryDetailsTable 
                                inventoryRows={filteredInventoryRows} 
                                handleQuantityChange={handleQuantityChange} 
                                maxHeight="280px"
                              />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className={styles.panelBody} style={{ padding: "24px", textAlign: "center" }}>
                          <span className="text-muted fs-7">Track Inventory is turned off. Enable it to manage inventory details.</span>
                        </div>
                      )}
                    </div>

                    <VariantCustomFields
                      values={values}
                      setFormikState={setFormikState}
                      mode={isEdit ? "edit" : "create"}
                      allCustomFields={allCustomFields}
                      editCustomFields={editCustomFields}
                    />

                    {isEdit && activeVariantId && (
                      <Box sx={{ width: "100%", mt: 3 }}>
                        <Card
                          sx={{
                            borderRadius: "8px",
                            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                            border: "1px solid #ddd",
                            backgroundColor: "#fff"
                          }}
                        >
                          <Box sx={{ borderBottom: "1px solid #e0e0e0", px: 2, pt: 1 }}>
                            <StyledTabs value={activeTab} onChange={handleChangeTab}>
                              <StyledTab label="Inventory Logs" />
                              <StyledTab label="Price Logs" />
                            </StyledTabs>
                          </Box>

                          <CardContent sx={{ p: 2 }}>
                            <>
                              {activeTab === 0 && (
                                <InventoryLogs 
                                  isProductDetailPage={true} 
                                  variantId={activeVariantId}
                                />
                              )}
                              {activeTab === 1 && (
                                <PriceLogs 
                                  isProductDetailPage={true} 
                                  variantId={activeVariantId}
                                />
                              )}
                            </>
                          </CardContent>
                        </Card>
                      </Box>
                    )}

                    {showInventoryPopup && (
                      <LocationModal
                        show={showInventoryPopup}
                        onClose={() => setShowInventoryPopup(false)}
                        onSave={(locs) => handleSaveInventoryLocations(locs)}
                      />
                    )}

                    {showImageModal && (
                      <SelectProductImageModal
                        show={showImageModal}
                        onClose={() => setShowImageModal(false)}
                        isSingleSelect={true}
                        initialSelected={
                          values.selectedImages?.[0]
                            ? [
                                {
                                  fileName: values.selectedImages[0].fileName || "selected",
                                  imageUrl:
                                    values.selectedImages[0].imageUrl ||
                                    values.selectedImages[0].url,
                                  url:
                                    values.selectedImages[0].url ||
                                    values.selectedImages[0].imageUrl,
                                  id: values.selectedImages[0].id,
                                },
                              ]
                            : []
                        }
                        onSelect={(imgs) => {
                          if (imgs && imgs.length > 0) {
                            const img = imgs[0];
                            setFieldValue("selectedImages", [
                              {
                                fileName: img.fileName,
                                imageUrl: img.imageUrl || img.url,
                                url: img.url || img.imageUrl,
                                id: img.id,
                                type: "image/*",
                                size: img.size || 0,
                                isExistingVariantImage: false, // important
                              },
                            ]);
                          } else {
                            setFieldValue("selectedImages", []);
                          }
                          setShowImageModal(false);
                        }}
                      />
                    )}
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default VariantEditorPremium;

